/**
 * GS1 Digital Link / Application Identifier parser
 *
 * Supported input formats:
 *   Raw AI string      : 01019594912345621F2L9Q3X7N6Y8
 *   Raw AI + hash      : 01019594912345621F2L9Q3X7N6Y8#suspected
 *   GS1 Digital Link   : https://example.com/01/0195949123456/21/F2L9Q3X7N6Y8
 *
 * Returns a clean React Router path such as:
 *   /01/0195949123456/21/F2L9Q3X7N6Y8
 *   /01/0195949123456
 * or null when the input cannot be parsed as GS1.
 *
 * NOTE: The URL fragment (#suspected / #expired) is intentionally NOT
 * included in the returned path — callers must append it separately so the
 * path itself stays clean for route matching.
 */
export const parseGS1 = (raw) => {
  if (!raw || raw.length <= 16) return null;

  // ── Step 1: Strip URL fragment (#suspected, #expired …) BEFORE normalising
  //            so it never contaminates GTIN / serial / lot values.
  const withoutFragment = raw.includes('#')
    ? raw.substring(0, raw.indexOf('#'))
    : raw;

  if (!withoutFragment.startsWith('01') && !withoutFragment.includes('/01/')) {
    return null;
  }

  // ── Step 2: Normalise – collapse GS1 Digital Link URL prefix, then keep
  //            only alphanumeric chars (Application Identifiers + their values).
  const normalised = withoutFragment
    .replace(/.*\/01\//, '01')       // strip URL prefix up to /01/
    .replace(/[^a-zA-Z0-9]/g, '');  // remove separators / slashes / spaces

  if (!normalised.startsWith('01')) return null;

  // ── Step 3: Detect GTIN length (13 or 14 digits) by peeking at the
  //            Application Identifier that follows the GTIN field.
  //            Index 15 (0-based) is right after a 13-digit GTIN (2 + 13 = 15).
  const gtinLength = (
    normalised.substring(15, 17) === '21' ||
    normalised.substring(15, 17) === '10'
  ) ? 13 : 14;

  const ai1  = normalised.substring(0, 2);              // always '01'
  const gtin = normalised.substring(2, 2 + gtinLength); // GTIN digits
  const rest = normalised.substring(2 + gtinLength);    // remaining AI+value pairs

  // GTIN only — no further Application Identifiers present
  if (rest.length < 2) return `/${ai1}/${gtin}`;

  const ai2  = rest.substring(0, 2); // e.g. '21' (serial) or '10' (lot)
  const val2 = rest.substring(2);    // value for ai2 (clean — no fragment chars)

  return `/${ai1}/${gtin}/${ai2}/${val2}`;
};
